import { axiosClientAdmin } from '@/lib/auth';
import {
  DeviceType,
  IntegrationFullDto,
  IntegrationLimitedDto,
  PartialIntegrationFieldDto,
  SourceType,
} from '@/lib/integrationTypes/integrationTypes';
import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';

export type IntegrationsListed = {
  integrations: IntegrationLimitedDto[];
};

export type CreateIntegrationBody = {
  name: string;
  integrationType: DeviceType;
  sourceType: SourceType;
  description?: string;
  brand?: string;
  docs?: string;
  smtpSource?: boolean;
  webhookSource?: boolean;
};

export type IntegrationFetched = {
  integration: IntegrationFullDto;
};

export type IntegrationSaved = IntegrationFetched & {
  code: string;
  recompile: boolean;
  compileLog?: string;
  compiledCode?: string;
  success: boolean;
  message: string | null;
  responseCode: string;
};

export type IntegrationLibraryFetched = {
  watchfulLibrary: string;
  integrationLibrary: string;
};

export type UpdateFieldsForm = {
  integrationFields: PartialIntegrationFieldDto[];
};

export type SaveCodeForm = {
  recompile?: boolean;
  code: string;
};

export type UseMutationOptions = {
  queryClient: QueryClient;
};

export type UpdateIntegrationTriggersForm = {
  webhook?: boolean;
  smtp?: boolean;
  watchfulLifecycle?: boolean;
};

export type UpdateIntegrationKeyForm = {
  key?: string | null;
};

export const useIntegrations = () =>
  useQuery({
    queryKey: ['integrations'],
    queryFn: () =>
      axiosClientAdmin.get<IntegrationsListed>('/Integration/manage').then((res) => res.data),
  });

export const useCreateIntegration = ({ queryClient }: UseMutationOptions) =>
  useMutation({
    mutationFn: (newIntegration: CreateIntegrationBody) =>
      axiosClientAdmin.post('/Integration/manage', newIntegration),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

type UseIntegrationQueryOptions = {
  id: string;
};

export const useIntegration = ({ id }: UseIntegrationQueryOptions) =>
  useQuery({
    queryKey: ['integrations', id],
    queryFn: () =>
      axiosClientAdmin.get<IntegrationFetched>(`/Integration/manage/${id}`).then((res) => res.data),
  });

export const useIntegrationsLibraries = ({ id }: UseIntegrationQueryOptions) =>
  useQuery({
    queryKey: ['integrations', id, 'libraries'],
    queryFn: () =>
      axiosClientAdmin
        .get<IntegrationLibraryFetched>(`/Integration/manage/${id}/library`)
        .then(({ data }) => data),
  });

type UseIntegrationMutationOptions = UseIntegrationQueryOptions & UseMutationOptions;

export type PollStatusDto = {
  id: string;
  deviceId: string;
  nextRun: number;
  intervalSeconds: number;
  metadata: string;
};

export type GetPollingStatusResponse = {
  totalPolls: number;
  duePolls: number;
  oldestPoll: PollStatusDto | null;
};

export const usePollingStatus = (pollingIntervalMs: number) =>
  useQuery({
    queryKey: ['polling-status'],
    queryFn: () =>
      axiosClientAdmin.get<GetPollingStatusResponse>('/Integration/Polling/status').then((res) => res.data),
    refetchInterval: pollingIntervalMs,
  });

export const useUpdateIntegrationFields = ({ id, queryClient }: UseIntegrationMutationOptions) =>
  useMutation({
    mutationFn: (options: UpdateFieldsForm) =>
      axiosClientAdmin.post(`/Integration/manage/${id}/fields`, options),
    mutationKey: ['integrations', id, 'updateFields'],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations', id],
      });
    },
  });

export const useSaveCodeMutation = ({ id }: UseIntegrationQueryOptions) =>
  useMutation({
    mutationKey: ['integrations', id, 'updateCode'],
    mutationFn: (saveCode: SaveCodeForm) =>
      axiosClientAdmin
        .post<IntegrationSaved>(`/Integration/manage/${id}/code`, saveCode)
        .then((res) => res.data),
  });

export const useDeleteIntegrationMutation = ({ id, queryClient }: UseIntegrationMutationOptions) =>
  useMutation({
    mutationFn: () => axiosClientAdmin.delete(`/Integration/manage/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

export const useUpdateTriggersMutation = ({ id, queryClient }: UseIntegrationMutationOptions) =>
  useMutation({
    mutationKey: ['integrations', id, 'updateTriggers'],
    mutationFn: (body: UpdateIntegrationTriggersForm) =>
      axiosClientAdmin.post(`/Integration/manage/${id}/triggers`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['integrations', id],
      });
    },
  });

export const useUpdateIntegrationKey = ({ id, queryClient }: UseIntegrationMutationOptions) =>
  useMutation({
    mutationKey: ['integrations', id, 'updateKey'],
    mutationFn: (body: UpdateIntegrationKeyForm) =>
      axiosClientAdmin.put(`/Integration/manage/${id}/key`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations', id] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

export const useClearIntegrationPolls = ({ id }: UseIntegrationQueryOptions) =>
  useMutation({
    mutationKey: ['integrations', id, 'clearPolls'],
    mutationFn: () => axiosClientAdmin.delete(`/Integration/manage/${id}/polls`),
  });

export const useClearIntegrationKVStore = ({ id }: UseIntegrationQueryOptions) =>
  useMutation({
    mutationKey: ['integrations', id, 'clearKVStore'],
    mutationFn: () => axiosClientAdmin.delete(`/Integration/manage/${id}/kvstore`),
  });
